import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import Information20 from '@carbon/icons-react/es/information/20';
import Link16 from '@carbon/icons-react/es/link/16';
import Unlink16 from '@carbon/icons-react/es/unlink/16';
import { Space, Modal, Divider as DividerAtom, Typography } from 'antd';
import React, { ReactElement, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory, useParams } from 'react-router';
import styled from 'styled-components';
import { HubspotConfig, HubspotIntegrationId } from 'api/integrations/hubspot';
import { Button } from 'components/atoms/button';
import { InlineNotification } from 'components/atoms/inline-notification';
import { InlineNotificationType } from 'components/atoms/inline-notification/inline-notification-types';
import { FeatureDiscovery } from 'components/molecules/feature-discovery';
import { ProductiveHeading06 } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import {
  useDisconnectHubspotMutation,
  useHubspotAuthenticateQuery,
} from 'queries/hubspot-integration';
import { HubspotLayout } from 'routes/settings/integrations/hubspot/hubspot-layout';
import { IntegrationStatusTypes } from '../../authentication-types';
import { AlertButton, Message } from '../../styles';

const { Text } = Typography;

export const Heading = styled.h6`
  ${ProductiveHeading06};
  margin-bottom: var(--spacing-02);
  padding-bottom: 16px;
`;

const Container = styled.div`
  padding: 60px;
  padding-top: 40px;
`;

const InfoGraphicSection = styled.div`
  width: 100%;
  display: flex;
`;

const InfoGraphicSectionDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-left: 32px;
  width: 25rem;
`;

const Iframe = styled.iframe`
  height: 400px;
  width: 57%;

  header {
    display: none;
  }
`;

const Divider = styled(DividerAtom)`
  margin: 32px 0;
`;

const StyledUl = styled.ul`
  padding-left: 18px;
  margin-bottom: 0;
`;

const {
  Section: { Content },
} = FeatureDiscovery;

const HubspotSettingLayout = ({
  btnText,
  icon,
  href,
  disabled,
  loading,
  heading,
}: {
  btnText: ReactElement;
  icon: ReactElement;
  href: string | undefined;
  disabled: boolean;
  loading: boolean;
  heading?: ReactElement;
}) => (
  <div>
    {heading && <Heading>{heading}</Heading>}
    <Button
      type="primary"
      icon={icon}
      href={href}
      disabled={disabled}
      loading={loading}
      iconPlacement="right"
    >
      {btnText}
    </Button>
  </div>
);

export const HubspotSettings = ({ data }: { data?: HubspotConfig }) => {
  const { integrationId } = useParams<HubspotIntegrationId>();
  const history = useHistory();

  const { mutate: disconnectHubspot, isLoading } =
    useDisconnectHubspotMutation();

  const { isLoading: isLoadingAuth, data: authDetails } =
    useHubspotAuthenticateQuery({
      enabled:
        integrationId === 'new' ||
        data?.integrationStatus === IntegrationStatusTypes.Expired,
    });

  const [showDisconnectButton, setShowDisconnectButton] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  const onMouseHandle = (status = false) => {
    setShowDisconnectButton(status);
  };
  const handleDisconnect = () => {
    setShowDisconnectModal(true);
  };
  const onDisconnect = async () => {
    if (integrationId !== 'new') {
      await disconnectHubspot(Number(integrationId), {
        onSuccess: () => {
          history.push('/settings/integrations/');
        },
      });
    }
  };

  const alertMessage = useTranslation('integration.alert.error.message');
  const reAuthMessage = useTranslation(
    'settings.integrations.sf.settings.alert.button.message',
  );
  const reAuthenticate = useTranslation(
    'settings.integration.alert.button.message',
  );

  return (
    <HubspotLayout integrationStatus={data?.integrationStatus}>
      <Container>
        {integrationId === 'new' ? (
          <>
            <HubspotSettingLayout
              heading={
                <FormattedMessage id="settings.integrations.hubspot.settings.title" />
              }
              icon={<ArrowRight16 />}
              href={authDetails?.oauthUrl}
              disabled={authDetails == null}
              btnText={
                <FormattedMessage id="settings.integrations.hubspot.settings.connect" />
              }
              loading={isLoadingAuth}
            />
            <Divider />
          </>
        ) : (
          <>
            {data?.integrationStatus === IntegrationStatusTypes.Expired && (
              <InlineNotification
                msg={
                  <Message>
                    <strong>{alertMessage}</strong>
                    {reAuthMessage}
                  </Message>
                }
                type={InlineNotificationType.Error}
                button={
                  <AlertButton
                    size="small"
                    type="tertiary"
                    href={authDetails?.oauthReauthUrl}
                  >
                    {reAuthenticate}
                  </AlertButton>
                }
                icon={<Information20 />}
              />
            )}
            <Space direction="vertical" align="start">
              <Heading>
                <FormattedMessage id="settings.integrations.hubspot.settings.title" />
              </Heading>
              <Button
                type="primary"
                icon={showDisconnectButton ? <Unlink16 /> : <Link16 />}
                iconPlacement="right"
                onClick={handleDisconnect}
                onMouseEnter={() => onMouseHandle(true)}
                onMouseLeave={() => onMouseHandle(false)}
                danger={!!showDisconnectButton}
              >
                {showDisconnectButton ? (
                  <FormattedMessage id="disconnect" />
                ) : (
                  <FormattedMessage id="settings.integrations.hubspot.settings.connected" />
                )}
              </Button>
              {data?.meta.oauthAuthenticatorEmail ? (
                <>
                  <Text>
                    {data?.meta.oauthAuthenticatorAccountUrl ? (
                      <FormattedMessage
                        id="settings.integrations.hubspot.settings.connected.to.as"
                        values={{
                          connector: (
                            <b>{data?.meta.oauthAuthenticatorEmail}</b>
                          ),
                          company: (
                            <b>
                              {
                                data?.meta.oauthAuthenticatorAccountUrl?.split(
                                  'https://',
                                )[1]
                              }
                            </b>
                          ),
                        }}
                      />
                    ) : (
                      <FormattedMessage
                        id="settings.integrations.hubspot.settings.connected.as"
                        values={{
                          connector: (
                            <b>{data?.meta.oauthAuthenticatorEmail}</b>
                          ),
                        }}
                      />
                    )}
                  </Text>
                </>
              ) : null}
            </Space>
            <Divider />

            <Modal
              visible={showDisconnectModal}
              title={
                <FormattedMessage id="settings.integrations.hubspot.disconnect.title" />
              }
              confirmLoading={false}
              onCancel={() => setShowDisconnectModal(false)}
              onOk={onDisconnect}
              okText={<FormattedMessage id="disconnect" />}
              okType="danger"
              okButtonProps={{ loading: isLoading }}
              cancelButtonProps={{ disabled: isLoading }}
              closable={!isLoading}
            >
              <p>
                <FormattedMessage id="settings.integrations.hubspot.disconnnect.content" />
              </p>
            </Modal>
          </>
        )}
        <InfoGraphicSection>
          <Iframe
            src="https://www.youtube.com/embed/3aLYVW7lsmY"
            title="HubSpot Integration"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
          <InfoGraphicSectionDescription>
            <Content.Title>
              <FormattedMessage id="settings.integrations.hubspot.feature_discovery.title" />
            </Content.Title>

            <FormattedMessage id="settings.integrations.hubspot.feature_discovery.description" />

            <StyledUl>
              <FormattedMessage
                id="settings.integrations.hubspot.feature_discovery.steps"
                values={{
                  li: (chunks) => <li>{chunks}</li>,
                  b: (chunks) => <b>{chunks}</b>,
                }}
              />
            </StyledUl>

            <FormattedMessage id="settings.integrations.hubspot.feature_discovery.closingStatement" />
          </InfoGraphicSectionDescription>
        </InfoGraphicSection>
      </Container>
    </HubspotLayout>
  );
};
